import { RoleEnum } from "../enums/EnterpriseEnums";
import { SubscriptionPlanCategoryEnum, SubscriptionStatusEnum } from "../enums/PartnerSubscription";
import { Partner } from "../interfaces/partner/PartnerProfile";

// Partner Checks
export const isAdminUser = (partner: Partner): boolean => {
    return partner?.role === RoleEnum.HR || partner?.role === RoleEnum.ADMIN;
}

export const isOwnerUser = (partner: Partner): boolean => {
    return partner?.role === RoleEnum.ADMIN;
}

export const showEnterpriseJobsList = (partner: Partner): boolean => {
    return partner?.featuresControls?.eligibleForEnterpriseViews || (partner?.currentSubscription?.subscriptionCategory === SubscriptionPlanCategoryEnum.ENTERPRISE);
}

export const supportsFinalist = (partner: Partner): boolean => {
    return partner?.featuresControls?.eligibleForEnterpriseViews;
}

export const supportsMassHiring = (partner: Partner): boolean => {
    return partner?.featuresControls?.eligibleForMassHiringViews;
}

export const supportsScreeningQuestions = (partner: Partner): boolean => {
    return partner?.featuresControls?.eligibleForScreeningQuestions;
}

export const supportsInterviewEvaluation = (partner: Partner): boolean => {
    return partner?.featuresControls?.eligibleForInterviewEvaluation === true;
}

export const supportsTalentAcquisitionSync = (partner: Partner): boolean => {
    return partner?.featuresControls?.eligibleForTalentAcquisitionSync === true;
}

export const supportsCvSearch = (partner: Partner): boolean => {
    return partner?.featuresControls?.eligibleForCvSearch;
}

export const supportsJobTemplates = (partner: Partner): boolean => {
    return partner?.featuresControls?.eligibleForJobTemplates;
}

/** Free job titles: when enabled the employer can type a custom title, otherwise they must select from the lookups. */
export const supportsFreeJobTitle = (partner: Partner): boolean => {
    return true;
}

export const hadOrHasActiveEnterpriseSubscription = (partner: Partner): boolean => {
    return partner?.currentSubscription?.subscriptionCategory === SubscriptionPlanCategoryEnum.ENTERPRISE;
}

export const hasActiveEnterpriseSubscription = (partner: Partner): boolean => {
    return partner?.currentSubscription?.subscriptionStatus === SubscriptionStatusEnum.ACTIVE && partner?.currentSubscription?.subscriptionCategory === SubscriptionPlanCategoryEnum.ENTERPRISE;
}

export const expiredEnterpriseSubscription = (partner: Partner): boolean => {
    return partner?.currentSubscription?.subscriptionStatus !== SubscriptionStatusEnum.ACTIVE && partner?.currentSubscription?.subscriptionCategory === SubscriptionPlanCategoryEnum.ENTERPRISE;
}

export const isEnterpriseUserRole = (partner: Partner): boolean => {
    return partner?.role === RoleEnum.USER && partner?.currentSubscription?.subscriptionCategory === SubscriptionPlanCategoryEnum.ENTERPRISE;
}

export const canPartnerUserPay = (partner: Partner): boolean => {
    return partner?.role === RoleEnum.ADMIN || partner?.role === RoleEnum.HR || (partner?.role === RoleEnum.USER && partner?.currentSubscription?.subscriptionCategory !== SubscriptionPlanCategoryEnum.ENTERPRISE);
}

/** Active paid Job Post plan (not Free, not merely having a subscription record). */
export const isPaidUser = (partner?: Partner | null): boolean => {
    const sub = partner?.currentSubscription;
    return (
        !!sub &&
        sub.subscriptionStatus === SubscriptionStatusEnum.ACTIVE &&
        sub.subscriptionCategory !== SubscriptionPlanCategoryEnum.FREE
    );
}

export const canPartnerUserSubscribeInApp = (partner: Partner): boolean => {
    return partner?.currentSubscription?.subscriptionCategory !== SubscriptionPlanCategoryEnum.ENTERPRISE;
}

const PLAN_UPGRADE_LADDER: SubscriptionPlanCategoryEnum[] = [
    SubscriptionPlanCategoryEnum.CORE,
    SubscriptionPlanCategoryEnum.RISE,
    SubscriptionPlanCategoryEnum.GROWTH,
    SubscriptionPlanCategoryEnum.ENTERPRISE,
];

/** True when the app is running inside the PMA (Flutter) webview. */
export const isPmaWebview = (): boolean =>
    typeof window !== "undefined" && !!(window as any).flutter_inappwebview;

/** Public PMA payment link (`/#/payment/company/:companyId`), HashRouter or path. */
export const isPmaPaymentPage = (): boolean => {
    if (typeof window === "undefined") return false;
    const href = `${window.location.pathname}${window.location.hash}`;
    return href.includes("/payment/company/");
};

/** Public flows where a 401 must not redirect to login (PMA payment links, email verify). */
export const isAuthOptionalPage = (): boolean => {
    if (typeof window === "undefined") return false;
    const href = window.location.href || "";
    return (
        href.includes("email-success") ||
        href.includes("/payment/company/") ||
        href.includes("checkout-details-pma")
    );
};

/** Next higher Job Post plan: Basic → Premium → Premium Plus → Enterprise. */
export const getNextPlan = (partner: Partner) => {
    const current = partner?.currentSubscription?.subscriptionCategory;
    const idx = PLAN_UPGRADE_LADDER.indexOf(current);
    if (idx === -1) return SubscriptionPlanCategoryEnum.CORE;
    return PLAN_UPGRADE_LADDER[Math.min(idx + 1, PLAN_UPGRADE_LADDER.length - 1)];
};

/**
 * Default selected plan for paywalls / pricing.
 * PWA new unsubscribed → Premium; PMA new unsubscribed → Basic;
 * expired → previous plan; paid hitting a limit → next higher plan.
 */
export const getPaywallDefaultPlan = (
    partner?: Partner | null,
    opts?: { isPma?: boolean }
): SubscriptionPlanCategoryEnum => {
    const isPma = opts?.isPma ?? isPmaWebview();
    const category = partner?.currentSubscription?.subscriptionCategory;

    if (isPaidUser(partner) && category) {
        return getNextPlan(partner);
    }

    if (
        category
        && category !== SubscriptionPlanCategoryEnum.FREE
        && category !== SubscriptionPlanCategoryEnum.WATAN
    ) {
        return category;
    }

    return isPma ? SubscriptionPlanCategoryEnum.CORE : SubscriptionPlanCategoryEnum.RISE;
};

export const isEnterprisePartnerMassHiring = (partner: Partner): boolean => {
    return partner?.currentSubscription?.subscriptionCategory === SubscriptionPlanCategoryEnum.ENTERPRISE && partner?.featuresControls?.eligibleForMassHiringViews && partner?.currentSubscription?.subscriptionStatus === SubscriptionStatusEnum.ACTIVE;
}

export const isEnterprisePartnerWhatsappEnabled = (partner: Partner): boolean => {
    return partner?.currentSubscription?.subscriptionCategory === SubscriptionPlanCategoryEnum.ENTERPRISE && partner?.featuresControls?.eligibleForWhatsapp && partner?.currentSubscription?.subscriptionStatus === SubscriptionStatusEnum.ACTIVE;
}

export const needToConfigureWhatsapp = (partner: Partner): boolean => {
    return !partner?.whatsapp?.hasCompletedWhatsappSetup;
}

export const whatsappConfigured = (partner: Partner): boolean => {
    return partner?.whatsapp?.hasCompletedWhatsappSetup === true;
}

export const whatsapp360Configured = (partner: Partner): boolean => {
    return whatsappConfigured(partner)
        && partner?.whatsapp?.hasCompleted360WhatsappSetup === true;
}

export const isWhatsappConfiguredButPendingTemplateApproval = (partner: Partner): boolean => {
    return partner?.whatsapp?.systemTemplatesSeeded === true && !partner?.whatsapp?.systemTemplatesApproved;
}

export const isWhatsappReady = (partner: Partner): boolean => {
    // return true;
    return partner?.featuresControls?.eligibleForWhatsapp && partner?.whatsapp?.hasCompletedWhatsappSetup === true && partner?.whatsapp?.systemTemplatesApproved === true ? true : false
}

export const isInterviewSchedulingEnabled = (partner: Partner): boolean => {
    return partner?.featuresControls?.eligibleForInterviewInvitation === true;
}

/** Onboarding feature: when backend is not ready, show for everyone (true). When ready, use partner flag. */
export const isOnboardingEnabled = (partner: Partner): boolean => {
    return partner?.featuresControls?.eligibleForOnboarding === true;
};

export const shouldShowOnboardingNavigation = (partner: Partner, jobList: any[]): boolean => {
    return isOnboardingEnabled(partner);
};

